import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { authStore } from "./authStore";
import type { AuthSession, AuthUser } from "./types";

interface AuthContextValue {
  session: AuthSession | null;
  user: AuthUser | null;
  userId: string | null;
  refresh: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readSnapshot(): { session: AuthSession | null; user: AuthUser | null } {
  const session = authStore.getSession();
  const user = session ? authStore.getUserById(session.userId) : null;
  return { session, user };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [snap, setSnap] = useState(readSnapshot);

  useEffect(() => authStore.subscribe(() => setSnap(readSnapshot())), []);

  const refresh = useCallback(() => setSnap(readSnapshot()), []);
  const logout = useCallback(() => {
    void authStore.logout().then(() => setSnap(readSnapshot()));
  }, []);

  useEffect(() => {
    void authStore.refreshFromServer().then((ok) => {
      if (ok) setSnap(readSnapshot());
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session: snap.session,
      user: snap.user,
      userId: snap.session?.userId ?? null,
      refresh,
      logout,
    }),
    [snap, refresh, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
